import { useFeatureSwitch } from "@app/shared/posthog/useFeatureSwitch";
import { currentUserRoleAtom } from "@app/store/userStore";
import { UserRole } from "@generated/client/graphql";
import { useAtomValue } from "jotai";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useMemo } from "react";
import { useModuleConfiguration } from "./data";
import { getAdminNavigation, getContributorNavigation } from "./utils";

export const useAdminNavigation = () => {
    const { moduleConfiguration } = useModuleConfiguration();

    const isSupplierModuleActiveFlag = useMemo(() => {
        return moduleConfiguration?.some(
            (module) =>
                module.moduleId === "supplier-module" && module.isActive,
        );
    }, [moduleConfiguration]);

    const legacySupplierFlag = useFeatureSwitch("legacySupplier");

    const isQuestionnairesModuleActiveFlag = useMemo(() => {
        return moduleConfiguration?.some(
            (module) =>
                module.moduleId === "questionnaire-module" && module.isActive,
        );
    }, [moduleConfiguration]);

    const isFrameworkModuleActiveFlag = useMemo(() => {
        return moduleConfiguration?.some(
            (module) =>
                module.moduleId === "framework-module" && module.isActive,
        );
    }, [moduleConfiguration]);

    const isKnowledgeBaseActiveFlag = useMemo(() => {
        return moduleConfiguration?.some(
            (module) =>
                (module.moduleId === "questionnaire-module" &&
                    module.isActive) ||
                (module.moduleId === "framework-module" &&
                    module.isActive &&
                    !!module.addOns
                        .filter((addOn) => addOn.isActive)
                        .filter(
                            (addOn) =>
                                addOn.moduleId !== "evs" &&
                                addOn.moduleId !== "csrd",
                        ).length),
        );
    }, [moduleConfiguration]);

    const sustainabilityCenterFlag = useFeatureFlagEnabled(
        "sustainabilityCenter",
    );

    const requirementsFlag = useFeatureFlagEnabled("legacyEcovadis");
    const legacyObjectivesFlag = useFeatureFlagEnabled("legacyObjectives");
    const { role, isPending } = useAtomValue(currentUserRoleAtom);

    const items = useMemo(() => {
        if (isPending) {
            return [];
        }
        if (role === UserRole.Admin || role === UserRole.BeavrAdmin) {
            return getAdminNavigation(
                sustainabilityCenterFlag,
                isSupplierModuleActiveFlag,
                legacySupplierFlag,
                legacyObjectivesFlag,
                isQuestionnairesModuleActiveFlag,
                isKnowledgeBaseActiveFlag,
                isFrameworkModuleActiveFlag,
                requirementsFlag,
            );
        }
        if (role === UserRole.Contributor) {
            return getContributorNavigation();
        }
        return [];
    }, [
        role,
        isPending,
        isSupplierModuleActiveFlag,
        legacySupplierFlag,
        sustainabilityCenterFlag,
        legacyObjectivesFlag,
        isQuestionnairesModuleActiveFlag,
        isKnowledgeBaseActiveFlag,
        isFrameworkModuleActiveFlag,
        requirementsFlag,
    ]);

    return { items, isPending };
};
